import { Reveal } from "@components/ui";
import dashboard from "@assets/images/new-dashboard-with-bg.png";

/* ─── Icons ─── */

function TargetIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  );
}

function ZapIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

function TrendingIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  );
}

function WifiIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12.55a11 11 0 0114.08 0" />
      <path d="M1.42 9a16 16 0 0121.16 0" />
      <path d="M8.53 16.11a6 6 0 016.95 0" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  );
}

/* ─── Data ─── */

const features = [
  {
    title: "Accurate Readings",
    description: "No estimates. No manual errors. Every reading is precise.",
    icon: <TargetIcon />,
  },
  {
    title: "Automatic Alerts",
    description: "System generates alerts if consumption is unusually high.",
    icon: <AlertIcon />,
  },
  {
    title: "Faster Billing",
    description: "Invoices generated immediately after meter reading.",
    icon: <ZapIcon />,
  },
  {
    title: "Consumption History",
    description: "View up to 12 months of your usage data and trends.",
    icon: <TrendingIcon />,
  },
  {
    title: "Remote Reading",
    description: "No need for technicians to enter your unit.",
    icon: <WifiIcon />,
  },
];

/* ─── Component ─── */

export function SmartMeteringSection() {
  return (
    <section className="w-full bg-white py-12 md:py-16 lg:py-[120px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto">
        {/* Header */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-3 md:gap-4 mb-10 md:mb-14">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/[0.08] text-[#122d63] text-[13px] font-semibold tracking-[-0.02em] uppercase">
              Technology
            </span>
            <h2 className="text-center text-[28px] md:text-[40px] lg:text-[54px] tracking-[-0.04em] font-semibold text-[#122d63] leading-[1.1]">
              Smart Metering Technology
            </h2>
            <p className="text-center text-[14px] md:text-[16px] lg:text-[20px] tracking-[-0.04em] text-[#62636c] leading-[1.5] max-w-[700px]">
              GAZ uses advanced smart meters that transmit readings
              automatically — ensuring accuracy and eliminating estimated bills.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Left — features */}
          <Reveal direction="up">
            <div className="flex flex-col gap-3 md:gap-4">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="group flex items-center gap-4 p-4 md:p-5 rounded-[16px] bg-[#f9f9fb] hover:bg-white hover:shadow-[0_2px_16px_rgba(0,0,0,0.08)] border border-transparent hover:border-[#e8e9ed] transition-all duration-300"
                >
                  <div className="w-10 h-10 md:w-11 md:h-11 rounded-[10px] bg-[#122d63]/10 text-[#122d63] flex items-center justify-center shrink-0 group-hover:bg-[#122d63] group-hover:text-white transition-all duration-300">
                    {feature.icon}
                  </div>
                  <div>
                    <h4 className="text-[15px] md:text-[18px] lg:text-[20px] tracking-[-0.04em] font-medium text-[#1c1c1c] font-albert-sans">
                      {feature.title}
                    </h4>
                    <p className="text-[13px] md:text-[14px] lg:text-[16px] tracking-[-0.04em] text-[#62636c] font-albert-sans">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Right — image */}
          <Reveal direction="up" delay={200} className="flex justify-center">
            <div className="flex-[1.14]">
              <img src={dashboard} alt="process image" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
